import { useMutation } from '@apollo/client';
import MediaSelectorField, {
  Media,
} from '@website/components/forms/fields/MediaSelectorField';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import {
  EditTripItineraryItemsQuery,
  TripItineraryItemInput,
  UpdateTripItineraryItemDocument,
} from '@website/types/graphqlOperations';
import { Alert, Form, InputGroup, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

export function composeUpdateMutationInput(
  formData: TripItineraryItemFormInput,
): TripItineraryItemInput {
  return {
    ...formData,
    media: formData?.media?.map(({ id }) => ({
      id,
    })),
  };
}

export function composeUpdateItineraryFormInitialValues(
  itineraryItem: EditTripItineraryItemsQuery['trip']['itinerary']['items'][0],
  tripID: string,
): TripItineraryItemFormInput {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { id: _, ...itineraryItemWithoutID } = itineraryItem;
  const formMedia: TripItineraryItemFormInput['media'] =
    itineraryItem?.media?.items
      // remove media items where the image doesn't exist
      .filter(({ file }) => !!file)
      .map(({ id, file }) => ({
        id,
        file: {
          key: file?.key,
          bucket: file?.bucket,
        },
      }));
  return {
    ...itineraryItemWithoutID,
    media: formMedia ?? [],
    tripID,
  };
}

type TripItineraryItemFormInput = Omit<
  TripItineraryItemInput,
  'media' | 'id'
> & {
  media: Omit<Media, 'type'>[];
};

type Props = {
  itineraryItem: EditTripItineraryItemsQuery['trip']['itinerary']['items'][0];
  /**
   * This is a callback for when an item is saved, delete or update to refresh the ui
   */
  onChange: () => void;
  tripID: string;
  hostID: string;
};

export default function ItineraryForm({
  itineraryItem,
  onChange,
  tripID,
  hostID,
}: Props): JSX.Element {
  const [updateItinerary, { loading, error }] = useMutation(
    UpdateTripItineraryItemDocument,
    {
      update(cache, { data }) {
        // only update cache, if item is new, not existing one
        if (itineraryItem.id) {
          return;
        }
        const tripCacheID = cache.identify({
          __typename: 'Trip',
          id: tripID,
        });

        const itineraryItemCacheID = cache.identify({
          __typename: 'TripItineraryItem',
          id: data.updateTripItineraryItem.id,
        });

        cache.modify({
          id: tripCacheID,
          fields: {
            itinerary(cachedItineraryItems) {
              return {
                items: [
                  ...cachedItineraryItems.items,
                  { __ref: itineraryItemCacheID },
                ],
              };
            },
          },
        });
      },
    },
  );

  const formInitialValues = composeUpdateItineraryFormInitialValues(
    itineraryItem,
    tripID,
  );

  const form = useForm<TripItineraryItemFormInput>({
    mode: 'onBlur',
    defaultValues: formInitialValues,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const saveChanges = async (formData: TripItineraryItemFormInput) => {
    // notify parent
    const input = composeUpdateMutationInput(formData);
    await updateItinerary({
      variables: {
        input,
      },
    });
    if (!error) {
      onChange();
    }
  };

  return (
    <Form onSubmit={handleSubmit(saveChanges)}>
      {error && (
        <Row>
          <Alert className="col-12" variant="danger">
            Something went wrong when saving itinerary item
          </Alert>
        </Row>
      )}
      <Form.Group controlId="title">
        <Form.Label>Title *</Form.Label>
        <InputGroup>
          <InputGroup.Text>Day {itineraryItem.day}:</InputGroup.Text>
          <Form.Control
            type="text"
            {...register('title', { required: true })}
          />
        </InputGroup>
      </Form.Group>
      <Form.Group controlId="description">
        <Form.Label>Description *</Form.Label>
        <Form.Control
          as="textarea"
          {...register('description', { required: true })}
          rows={5}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>Images</Form.Label>
        <MediaSelectorField
          hostID={hostID}
          tripID={tripID}
          control={control}
          name="media"
          allowMultipleSelect
        />
      </Form.Group>
      <br />
      <Form.Group>
        <SubmitButton
          text="Submit"
          submittingLabel="Submitting"
          isSubmitting={isSubmitting || loading}
        />
      </Form.Group>
    </Form>
  );
}
